import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import HubSpotConnect from './HubSpotConnect';
import Usage from './Usage';
import RecentActivity from './RecentActivity';
import { useLocation, Link } from 'react-router-dom';
import {
  completeReservedDataLayerEvent,
  releaseReservedDataLayerEvent,
  reserveDataLayerEvent,
} from '../utils/analytics';

function Dashboard() {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [hubspotConnections, setHubspotConnections] = useState([]);
  const [usageStats, setUsageStats] = useState({ creditsUsed: 0, failedAttempts: 0 });
  const location = useLocation();

  useEffect(() => {
    if (user && user.token) {
      fetchCreditBalance();
      fetchHubSpotConnections();
      fetchUsageStats();
    }

    // Check for HubSpot connection success/failure
    const params = new URLSearchParams(location.search);
    const hubspotConnected = params.get('hubspot_connected');
    const reconnected = params.get('reconnected');
    
    if (reconnected === 'true') {
      setMessage('HubSpot connection refreshed successfully!');
      if (user && user.token) {
        fetchHubSpotConnections();
      }
    } else if (hubspotConnected === 'true') {
      setMessage('HubSpot connected successfully!');
      if (user && user.token) {
        fetchHubSpotConnections();
      }
    } else if (hubspotConnected === 'false') {
      setError('Failed to connect HubSpot. Please try again.');
    }

    // Check for successful payment
    const sessionId = params.get('session_id');
    if (sessionId) {
      setMessage('Payment successful! Your credits have been added to your account.');
      if (user && user.token) {
        fetchCreditBalance();
        const purchaseEventKey = `purchase:${sessionId}`;

        if (reserveDataLayerEvent(purchaseEventKey)) {
          axios.get(`${process.env.REACT_APP_API_URL}/api/auth/checkout-session/${sessionId}`, {
            headers: {
              'Authorization': `Bearer ${user.token}`,
              'Content-Type': 'application/json'
            }
          })
            .then((response) => {
              completeReservedDataLayerEvent(purchaseEventKey, {
                event: 'purchase',
                userEmail: user.email,
                checkout_session_id: response.data.checkoutSessionId,
                payment_type: response.data.mode === 'subscription' ? 'monthly' : 'oneTime',
                ecommerce: response.data.ecommerce,
              });
            })
            .catch((error) => {
              console.error('Failed to track purchase event:', error);
              releaseReservedDataLayerEvent(purchaseEventKey);
            });
        }
      }
    }
  }, [user, location]);

  const fetchCreditBalance = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/credit-balance`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      setCreditBalance(response.data.creditBalance);
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
    }
  };

  const fetchHubSpotConnections = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/hubspot-connections`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      setHubspotConnections(response.data.connections);
    } catch (error) {
      console.error('Failed to fetch HubSpot connections:', error);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/credit-usage-stats`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      setUsageStats(response.data);
    } catch (error) {
      console.error('Failed to fetch usage statistics:', error);
    }
  };

  const deleteHubSpotConnection = async (connectionId) => {
    if (window.confirm('Are you sure you want to delete this HubSpot connection?')) {
      try {
        const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/auth/hubspot-connection/${connectionId}`, {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Delete response:', response.data);
        setMessage('HubSpot connection deleted successfully');
        fetchHubSpotConnections(); // Refresh the connections list
      } catch (error) {
        console.error('Failed to delete HubSpot connection:', error.response ? error.response.data : error.message);
        setError(`Failed to delete HubSpot connection: ${error.response ? error.response.data.error : error.message}`);
      }
    }
  };

  const refreshCreditBalance = () => {
    fetchCreditBalance();
    fetchUsageStats();
    setMessage('Dashboard data refreshed');
  };

  // Format number with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="welcome-message">Welcome, <span className="user-email">{user.email}</span>!</p>
      </div>
      
      {message && (
        <div className="alert alert-success">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>{message}</span>
        </div>
      )}
      
      {error && (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="dashboard-grid">
        <div className="dashboard-card credit-balance-card">
          <div className="card-header" flex>
            <h2>Credit Balance</h2>
            <button onClick={refreshCreditBalance} className="btn-icon" title="Refresh Balance">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </button>
          </div>
          <div className="credit-balance">
            <div className="credit-amount">{creditBalance}</div>
            <div className="credit-label">Available Credits</div>
          </div>
          <div className="credit-action">
            <Link to="/credits" className="get-credits-btn">Get More Credits</Link>
          </div>
        </div>

        <div className="dashboard-card usage-stats-card">
          <div className="card-header">
            <h2>Your Usage</h2>
          </div>
          <div className="usage-stats">
            <div className="usage-stat">
              <div className="usage-value">{formatNumber(usageStats.creditsUsed)}</div>
              <div className="usage-label">Successful Lookups</div>
            </div>
            <div className="usage-stat">
              <div className="usage-value">{formatNumber(usageStats.failedAttempts)}</div>
              <div className="usage-label">Failed Attempts</div>
            </div>
          </div>
          <div className="usage-note">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span>Failed attempts are not charged against your credit balance.</span>
          </div>
        </div>
      </div>

      <div className="dashboard-card hubspot-connections-card">
        <div className="card-header">
          <h2>HubSpot Connections</h2>
        </div>
        
        {hubspotConnections.length > 0 ? (
          <div className="connections-table-container">
            <br></br>
            <div className="connections-info-note">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span>New portals are pre-configured with HubSpot's default City, State/Region, Country, and Timezone fields. Click <strong>Configure</strong> to customize your field mappings.</span>
            </div>
            <table className="connections-table">
              <thead>
                <tr>
                  <th>Portal ID</th>
                  <th>Connected On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {hubspotConnections.map((connection) => (
                  <tr key={connection.id}>
                    <td>{connection.portal_id}</td>
                    <td>{new Date(connection.created_at).toLocaleDateString()} at {new Date(connection.created_at).toLocaleTimeString()}</td>
                    <td className="actions-cell">
                      <Link to={`/configure-portal/${connection.portal_id}`} className="btn btn-secondary btn-sm">
                        Configure
                      </Link>
                      <button 
                        onClick={() => deleteHubSpotConnection(connection.id)} 
                        className="btn btn-danger btn-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
              </svg>
            </div>
            <p>No HubSpot portals connected yet.</p>
            <p className="empty-hint">Connect your HubSpot portal to start using ZipCode AutoFill.</p>
          </div>
        )}
        
        <div className="card-footer">
          <HubSpotConnect onConnect={fetchHubSpotConnections} />
        </div>
      </div>

      <RecentActivity />

      <Usage />
    </div>
  );
}

export default Dashboard;
